// AI 青铜套餐 —— 5 个本地规则函数
//
// 全部是纯函数，零外部依赖：
//   - 输入：当前 BP 状态 + 英雄列表 + 赛事数据
//   - 输出：结构化推荐 / 警告 / 评分
//
// 完全本地计算，零成本运营。
// 升级到白银/黄金后，会用相同的输入输出契约接 LLM。

import { getChampionAttrs, summarizeTeam } from './championAttributes';

// ============================================================
//  功能 1：优先级提示 PriorityHint
// ============================================================
// 当前回合 = ban → 推荐"必 ban 的高 OP 英雄"
// 当前回合 = pick → 推荐"高胜率 + 适合的英雄"
export const priorityHint = ({ currentStep, champions, usedIds, statLookup }) => {
  if (!currentStep || !champions || champions.length === 0) return null;
  const available = champions.filter((c) => !usedIds.has(c.id));

  const scored = available.map((c) => {
    const stat = statLookup ? statLookup(c) : null;
    if (!stat) return { champion: c, score: 0, stat: null };

    const pr = stat.pick_rate_pct || 0;
    const br = stat.ban_rate_pct || 0;
    const wr = stat.win_rate_pct || 50;

    let score = 0;
    if (currentStep.type === 'ban') {
      // ban 阶段：综合 P+B 优先（场上热度）
      score = pr * 1.5 + br * 1.0;
    } else {
      // pick 阶段：胜率 + 出场率，胜率权重更高
      score = wr * 1.5 + pr * 0.8;
    }
    return { champion: c, score, stat };
  });

  const top = scored
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 5);

  if (top.length === 0) return null;

  return {
    type: currentStep.type === 'ban' ? 'ban' : 'pick',
    team: currentStep.team,
    slot: currentStep.slot,
    candidates: top,
    reasoning: currentStep.type === 'ban'
      ? '按 1.5×PR + BR 综合热度排序，越靠前越值得优先 ban'
      : '按 1.5×WR + 0.8×PR 排序，胜率高的英雄优先考虑',
  };
};

// ============================================================
//  功能 2：位置漏洞扫描 RoleGapScanner
// ============================================================
export const roleGapScan = ({ teamPicks }) => {
  const picks = teamPicks.filter(Boolean);
  if (picks.length === 0) return null;

  const s = summarizeTeam(picks);
  const issues = [];

  // 伤害类型偏科
  if (s.picks >= 2) {
    if (s.adCount === 0 && s.apCount >= 2) {
      issues.push({ level: 'warn', text: '全队无物理输出，对方可以堆魔抗一把过' });
    }
    if (s.apCount === 0 && s.adCount >= 2) {
      issues.push({ level: 'warn', text: '全队无法术输出，对方可以堆物抗一把过' });
    }
  }

  // 缺前排
  if (s.picks >= 3 && s.totalFrontline <= 2) {
    issues.push({ level: 'warn', text: `前排能力薄弱 (frontline ${s.totalFrontline}/9)，团战易被切后排` });
  }

  // 缺控制
  if (s.picks >= 3 && s.totalCC <= 2) {
    issues.push({ level: 'warn', text: `硬控严重不足 (CC ${s.totalCC}/9)，难以接团或反开` });
  }

  // 强势期偏科
  if (s.picks >= 4) {
    if (s.scaling.early === 0 && s.scaling.mid <= 1) {
      issues.push({ level: 'info', text: '阵容全是后期型，需保护前 25 分钟不破塔' });
    }
    if (s.scaling.late === 0 && s.scaling.mid <= 1) {
      issues.push({ level: 'info', text: '阵容偏早期，需在 25 分钟前抓节奏速推' });
    }
  }

  // 缺开团
  if (s.picks >= 3 && s.totalEngage <= 1) {
    issues.push({ level: 'warn', text: `开团能力不足 (engage ${s.totalEngage})，建议至少补 1 名开团位` });
  }

  // 正反馈
  const compliments = [];
  if (s.picks >= 3 && s.totalCC >= 6) {
    compliments.push('硬控充沛，团战定位明确');
  }
  if (s.picks >= 3 && s.totalFrontline >= 6) {
    compliments.push('前排厚实，可以打多核团战');
  }
  if (s.picks >= 3 && s.adCount >= 2 && s.apCount >= 2) {
    compliments.push('AP/AD 混合，对方难以单一抗性应对');
  }

  return { summary: s, issues, compliments };
};

// ============================================================
//  功能 3：Counter Pick 提示 CounterPick
// ============================================================
export const counterPick = ({ enemyChampion, availableChampions, statLookup }) => {
  if (!enemyChampion || !availableChampions || availableChampions.length === 0) return null;
  const eAttr = getChampionAttrs(enemyChampion);
  if (!eAttr) return null;

  const scored = availableChampions.map((c) => {
    const a = getChampionAttrs(c);
    if (!a) return { champion: c, score: 0, reasons: [] };

    const reasons = [];
    let score = 0;

    // 伤害类型对位
    if (eAttr.damageType === 'AP' && a.damageType === 'AD') {
      score += 8;
      reasons.push('伤害类型相反，对方魔抗收益低');
    } else if (eAttr.damageType === 'AD' && a.damageType === 'AP') {
      score += 8;
      reasons.push('伤害类型相反，对方物抗收益低');
    }

    // 反先手
    if (eAttr.engage >= 2 && a.cc >= 2) {
      score += 5;
      reasons.push('对方先手强，需控制反手');
    }

    // 对方刺客 → 推荐前排 / 控制保护后排
    if (eAttr.scaling === 'early' && eAttr.engage === 0 && eAttr.frontline === 0) {
      if (a.frontline >= 2 || a.cc >= 2) {
        score += 6;
        reasons.push('对方为刺客类，前排/控制可保护后排');
      }
    }

    // 拖后期
    if (eAttr.scaling === 'early' && a.scaling === 'late') {
      score += 3;
      reasons.push('对方早期型，可拖到后期获胜');
    }

    // 版本胜率加分
    const stat = statLookup ? statLookup(c) : null;
    if (stat && stat.win_rate_pct >= 53) {
      score += 2;
      reasons.push(`版本胜率 ${stat.win_rate_pct.toFixed(1)}%`);
    }

    return { champion: c, score, reasons };
  });

  const top = scored
    .filter((x) => x.score >= 6)
    .sort((a, b) => b.score - a.score)
    .slice(0, 5);

  if (top.length === 0) return null;

  return { against: enemyChampion, candidates: top };
};

// ============================================================
//  功能 4：阵容综合评分 CompScore
// ============================================================
export const compScore = ({ bluePicks, redPicks }) => {
  const score = (picks) => {
    const list = picks.filter(Boolean);
    if (list.length === 0) {
      return { early: 0, mid: 0, late: 0, total: 0, breakdown: null };
    }
    const s = summarizeTeam(list);

    let early = list.length * 0.5 + s.scaling.early * 1.5;
    let mid   = list.length * 0.5 + s.scaling.mid * 1.5;
    let late  = list.length * 0.5 + s.scaling.late * 1.5;

    if (s.totalCC >= 6) { mid += 1; late += 0.5; }
    if (s.totalFrontline >= 6) { mid += 1; late += 0.5; }
    if (s.totalEngage >= 4) { mid += 0.5; }
    if (s.adCount >= 2 && s.apCount >= 2) { mid += 0.5; late += 0.5; }

    const norm = (v) => Math.min(10, Math.max(1, v));
    return {
      early: Number(norm(early).toFixed(1)),
      mid:   Number(norm(mid).toFixed(1)),
      late:  Number(norm(late).toFixed(1)),
      total: Number(norm((early + mid + late) / 3).toFixed(1)),
      breakdown: s,
    };
  };

  return { blue: score(bluePicks), red: score(redPicks) };
};

// ============================================================
//  功能 5：TopX OP 警告 OPWarning
// ============================================================
export const opWarning = ({ champions, usedIds, statLookup, topN = 8 }) => {
  if (!statLookup || !champions || champions.length === 0) return null;

  const scored = champions
    .map((c) => {
      const stat = statLookup(c);
      if (!stat) return null;
      const pr = stat.pick_rate_pct || 0;
      const br = stat.ban_rate_pct || 0;
      return { champion: c, stat, opScore: pr + br };
    })
    .filter(Boolean)
    .sort((a, b) => b.opScore - a.opScore)
    .slice(0, topN);

  const stillAlive = scored.filter((x) => !usedIds.has(x.champion.id));
  if (stillAlive.length === 0) return null;

  return {
    topN,
    alive: stillAlive,
    totalChecked: scored.length,
  };
};
